import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SecondaryButton } from "@financial-edge/shared";
import AuthAlert from "../components/auth/AuthAlert";
import AuthContextPanel from "../components/auth/AuthContextPanel";
import AuthShell from "../components/auth/AuthShell";
import PrimaryButton from "../components/layout/PrimaryButton";
import api from "../api/api";
import {
    getAuthErrorCode,
    getAuthRequestId,
    getOtpErrorMessage,
    isNetworkError,
} from "../utils/auth/authDiagnostics";
import { logAuthFlowWarning } from "../utils/auth/authFlowLogger";
import { buildBlockedSignInStatusState, isBlockedSignInInterception } from "../utils/auth/interceptedAccess";
import {
    buildLaunchSearch,
    formatIntentDestinationReference,
    getIntentDetails,
    getEffectiveLaunchContext,
    getPrimaryAuthChannel,
    parseIntent,
    persistLaunchContext,
    persistRememberedSignInEmail,
    resolveXPortalLayerIntentFromAuthResponse,
    resolveXPortalLayerReturnTo,
} from "../utils/auth/intent";
import {
    clearPendingVerificationContext,
    persistPendingVerificationContext,
    readPendingVerificationContext,
} from "../utils/auth/pendingVerification";
import { DEFAULT_OTP_LENGTH, getOtpLengthForChallenge, normaliseOtpLength } from "../utils/auth/otpLength";
import { signOutBrowserSession } from "../utils/session/browserSession";
import { useAuthPageMeta } from "../utils/ui/useAuthPageMeta";

function maskPhone(phone) {
    const digits = String(phone || "").replace(/\D/g, "");
    if (digits.length < 4) {
        return "your mobile number";
    }

    return `mobile ending in ${digits.slice(-4)}`;
}

function maskEmail(email) {
    const trimmed = String(email || "").trim();
    if (!trimmed.includes("@")) {
        return "your email address";
    }

    const [local, domain] = trimmed.split("@");
    const prefix = local.length <= 2 ? `${local[0] || ""}*` : `${local.slice(0, 2)}***`;
    return `${prefix}@${domain}`;
}

function normaliseOtpInput(value, otpLength) {
    return String(value || "").replace(/\D/g, "").slice(0, otpLength);
}

export default function VerifyCode() {
    const location = useLocation();
    const navigate = useNavigate();
    const launchContext = getEffectiveLaunchContext(location.search);
    const persistedVerificationContext = readPendingVerificationContext();
    const verificationState = {
        ...(persistedVerificationContext || {}),
        ...((location.state && typeof location.state === "object") ? location.state : {}),
    };
    const intent = parseIntent(verificationState.intent || launchContext.intent);
    const phone = verificationState.phone || null;
    const email = verificationState.email || null;
    const initialRequestId = verificationState.requestId || null;
    const initialChallengeId = verificationState.challengeId || null;
    const methodSelection = verificationState.methodSelection || null;
    const initialChannel = verificationState.channel || getPrimaryAuthChannel(intent);
    const initialEmailDestinationHint =
        verificationState.emailDestinationHint ||
        methodSelection?.email?.destinationHint ||
        null;
    const source = verificationState.source || launchContext.source;
    const returnTo = verificationState.returnTo || launchContext.returnTo;
    const brokerageId = verificationState.brokerageId || launchContext.brokerageId || null;
    const hasExplicitIntent = verificationState.hasExplicitIntent ?? launchContext.hasExplicitIntent;
    const rememberDevice = verificationState.rememberDevice === true || launchContext.rememberDevice === true;
    const initialFallbackReason = verificationState.fallbackReason || null;
    const initialOtpPreview = verificationState.otpPreview || null;
    const initialTestUserBypass = verificationState.testUserBypass === true;
    const initialOtpLength = normaliseOtpLength(
        verificationState.otpLength,
        initialChannel === "sms" ? 4 : DEFAULT_OTP_LENGTH
    );
    const intentDetails = getIntentDetails(intent, source);
    const codeInputRef = useRef(null);

    const [otpLength, setOtpLength] = useState(initialOtpLength);
    const [otpCode, setOtpCode] = useState("");
    const [activeRequestId, setActiveRequestId] = useState(initialRequestId);
    const [activeChallengeId, setActiveChallengeId] = useState(initialChallengeId);
    const [activeChannel, setActiveChannel] = useState(initialChannel);
    const [activeEmailDestinationHint, setActiveEmailDestinationHint] = useState(initialEmailDestinationHint);
    const [activeFallbackReason, setActiveFallbackReason] = useState(initialFallbackReason);
    const [activeOtpPreview, setActiveOtpPreview] = useState(initialOtpPreview);
    const [activeTestUserBypass, setActiveTestUserBypass] = useState(initialTestUserBypass);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [errorCode, setErrorCode] = useState("");
    const [errorRequestId, setErrorRequestId] = useState("");

    const [resendMessage, setResendMessage] = useState("");
    const [resendRemaining, setResendRemaining] = useState(60);

    useAuthPageMeta("Enter Verification Code");

    useEffect(() => {
        persistLaunchContext({
            intent,
            source,
            returnTo,
            brokerageId,
            hasExplicitIntent,
            rememberDevice,
        });
    }, [brokerageId, hasExplicitIntent, intent, rememberDevice, returnTo, source]);

    useEffect(() => {
        persistPendingVerificationContext({
            requestId: activeRequestId,
            challengeId: activeChallengeId,
            channel: activeChannel,
            phone,
            email,
            emailDestinationHint: activeEmailDestinationHint,
            intent,
            source,
            returnTo,
            brokerageId,
            hasExplicitIntent,
            rememberDevice,
            fallbackReason: activeFallbackReason,
            otpLength,
            otpPreview: activeOtpPreview,
            testUserBypass: activeTestUserBypass,
            methodSelection,
        });
    }, [
        activeChallengeId,
        activeRequestId,
        activeChannel,
        activeEmailDestinationHint,
        activeFallbackReason,
        activeOtpPreview,
        activeTestUserBypass,
        brokerageId,
        email,
        hasExplicitIntent,
        intent,
        methodSelection,
        otpLength,
        phone,
        rememberDevice,
        returnTo,
        source,
    ]);

    const isComplete = useMemo(() =>
        otpCode.length === otpLength && /^[0-9]+$/.test(otpCode),
    [otpCode, otpLength]);
    const deliveryTarget = activeChannel === "email"
        ? (activeEmailDestinationHint || maskEmail(email))
        : maskPhone(phone);
    const destinationReference = formatIntentDestinationReference(intentDetails.label);
    const verificationDescription = activeTestUserBypass
        ? `Use the ${otpLength}-digit ${activeOtpPreview ? "development test code shown below" : "configured development test code"} to verify your identity before opening ${destinationReference}.`
        : `We sent a ${otpLength}-digit code to ${deliveryTarget}. Enter it below to verify your identity before opening ${destinationReference}.`;
    const deliveryTargetLabel = activeTestUserBypass ? "Verification account" : "Code sent to";
    const verificationFooter = activeTestUserBypass
        ? `For this development test account, use ${activeOtpPreview ? "the code shown on this screen" : "the test code provided by your administrator"}. Financial Edge AX will never ask you to share this code by phone or email.`
        : "If the code expires or does not arrive, request a new one. Financial Edge AX will never ask you to share this code by phone or email.";

    useEffect(() => {
        codeInputRef.current?.focus();
    }, []);

    useEffect(() => {
        const hasSmsContext = activeChannel === "sms" && phone;
        const hasEmailContext = activeChannel === "email" && (email || activeEmailDestinationHint || phone);

        if (!activeRequestId || (!hasSmsContext && !hasEmailContext)) {
            clearPendingVerificationContext();
            navigate(`/login${buildLaunchSearch({ intent, source, returnTo, brokerageId })}`, {
                replace: true,
            });
        }
    }, [activeRequestId, activeChannel, activeEmailDestinationHint, brokerageId, email, intent, navigate, phone, returnTo, source]);

    useEffect(() => {
        if (resendRemaining <= 0) {
            return undefined;
        }

        const timer = window.setInterval(() => {
            setResendRemaining((seconds) => Math.max(0, seconds - 1));
        }, 1000);

        return () => window.clearInterval(timer);
    }, [resendRemaining]);

    const focusCodeInput = () => {
        codeInputRef.current?.focus();
    };

    const submitOtpCode = async (code) => {
        const expectedOtpPattern = new RegExp(`^\\d{${otpLength}}$`);

        if (!expectedOtpPattern.test(code)) {
            setError(`Enter the full ${otpLength}-digit verification code before continuing.`);
            setErrorCode("");
            setErrorRequestId("");
            focusCodeInput();
            return;
        }

        setLoading(true);
        setError("");
        setErrorCode("");
        setErrorRequestId("");

        setResendMessage("");

        try {
            if (!activeRequestId) {
                throw new Error("The verification request could not be found. Request a new code and try again.");
            }

            const verifyPayload = {
                code,
                intent,
                channel: activeChannel,
            };

            if (activeChallengeId) {
                verifyPayload.challengeId = activeChallengeId;
            }

            const verifyResult = await api.verifyOtp(verifyPayload);
            const resolvedIntent = resolveXPortalLayerIntentFromAuthResponse(verifyResult, intent, source);
            const resolvedReturnTo = resolveXPortalLayerReturnTo(returnTo, intent, resolvedIntent, source);
            const resolvedBrokerageId =
                verificationState.brokerageId ||
                launchContext.brokerageId ||
                verifyResult?.access?.applicationForm?.brokerageLink?.brokerageId ||
                verifyResult?.access?.sharedAccessContext?.brokerage?.brokerageId ||
                null;
            const resolvedLaunchSearch = buildLaunchSearch({
                intent: resolvedIntent,
                source,
                returnTo: resolvedReturnTo,
                brokerageId: resolvedBrokerageId,
            });

            if (isBlockedSignInInterception(verifyResult?.access)) {
                clearPendingVerificationContext();
                await signOutBrowserSession();
                navigate(`/auth-status${resolvedLaunchSearch || ""}`, {
                    replace: true,
                    state: buildBlockedSignInStatusState(verifyResult.access, {
                        intent: resolvedIntent,
                    }),
                });
                return;
            }

            clearPendingVerificationContext();
            if (activeChannel === "email") {
                persistRememberedSignInEmail(email);
            }
            navigate(`/portal-selection${resolvedLaunchSearch}`, {
                replace: true,
                state: {
                    intent: resolvedIntent,
                    returnTo: resolvedReturnTo,
                    resolution: verifyResult?.resolution || null,
                    preverifiedSession: {
                        identity: verifyResult?.identity || null,
                        session: verifyResult?.session || null,
                        access: verifyResult?.access || null,
                    },
                    verificationContext: {
                        phone,
                        email,
                        brokerageId: resolvedBrokerageId,
                    },
                    hasExplicitIntent,
                    rememberDevice,
                },
            });
        } catch (err) {
            logAuthFlowWarning("verification_failed", {
                intent,
                source,
                phone,
                email,
                message: err?.message || "unknown_error",
                code: err?.code || null,
                requestId: err?.requestId || null,
                offline: !navigator.onLine,
            });
            setError(getOtpErrorMessage(err));
            setErrorCode(getAuthErrorCode(err) || "");
            setErrorRequestId(getAuthRequestId(err) || "");
            setOtpCode("");

            focusCodeInput();
        } finally {
            setLoading(false);
        }
    };

    const applyOtpInput = (nextCode) => {
        setOtpCode(nextCode);
        setError("");
        setErrorCode("");
        setErrorRequestId("");

        setResendMessage("");

        if (nextCode.length === otpLength && !loading) {
            void submitOtpCode(nextCode);
        }
    };

    const handleChange = (event) => {
        const nextCode = normaliseOtpInput(event.target.value, otpLength);

        if (event.target.value !== nextCode) {
            event.target.value = nextCode;
        }

        applyOtpInput(nextCode);
    };

    const handlePaste = (event) => {
        const pastedDigits = normaliseOtpInput(event.clipboardData.getData("text"), otpLength);

        if (!pastedDigits) {
            return;
        }

        event.preventDefault();
        applyOtpInput(pastedDigits);
    };

    const handleConfirm = async (event) => {
        event?.preventDefault?.();
        await submitOtpCode(otpCode);
    };

    const handleCancel = () => {
        clearPendingVerificationContext();
        navigate(`/login${buildLaunchSearch({ intent, source, returnTo, brokerageId })}`, {
            replace: true,
        });
    };

    const handleResend = async () => {
        if (resendRemaining > 0 || loading) {
            return;
        }

        setError("");
        setErrorCode("");
        setErrorRequestId("");

        setResendMessage("");

        try {
            const otpResponse = await api.requestOtp({
                phone,
                email,
                intent,
                source,
                brokerageId,
                returnTo,
                channel: activeChannel,
            });
            const resendResolvedIntent = resolveXPortalLayerIntentFromAuthResponse(otpResponse, intent, source);
            const resendResolvedReturnTo = resolveXPortalLayerReturnTo(returnTo, intent, resendResolvedIntent, source);
            const resendLaunchSearch = buildLaunchSearch({
                intent: resendResolvedIntent,
                source,
                returnTo: resendResolvedReturnTo,
                brokerageId,
            });

            if (isBlockedSignInInterception(otpResponse?.access)) {
                clearPendingVerificationContext();
                await signOutBrowserSession();
                navigate(`/auth-status${resendLaunchSearch || ""}`, {
                    replace: true,
                    state: buildBlockedSignInStatusState(otpResponse.access, {
                        intent: resendResolvedIntent,
                    }),
                });
                return;
            }

            const nextOtpLength = getOtpLengthForChallenge(otpResponse?.challenge, otpLength);
            setOtpLength(nextOtpLength);
            setOtpCode("");
            setResendRemaining(60);
            const nextChannel = otpResponse?.challenge?.channel || activeChannel;
            const nextEmailDestinationHint = nextChannel === "email"
                ? (otpResponse?.challenge?.delivery?.redactedDestination || otpResponse?.methodSelection?.email?.destinationHint || activeEmailDestinationHint)
                : null;
            const nextFallbackReason = otpResponse?.challenge?.fallbackReason || null;
            const nextOtpPreview = otpResponse?.challenge?.otpPreview || null;
            const nextTestUserBypass = otpResponse?.challenge?.testUserBypass === true;
            const nextChallengeId = otpResponse?.challenge?.challengeId || null;
            const nextDeliveryTarget = nextChannel === "email"
                ? (nextEmailDestinationHint || maskEmail(email))
                : maskPhone(phone);

            setActiveChannel(nextChannel);
            setActiveRequestId(otpResponse?.requestId || null);
            setActiveChallengeId(nextChallengeId);
            setActiveEmailDestinationHint(nextEmailDestinationHint);
            setActiveFallbackReason(nextFallbackReason);
            setActiveOtpPreview(nextOtpPreview);
            setActiveTestUserBypass(nextTestUserBypass);
            setResendMessage(nextTestUserBypass
                ? `Use ${nextOtpPreview ? "the development test code shown below" : "the configured development test code"}. No email or SMS was sent for this test account.`
                : `A new code was sent to ${nextDeliveryTarget}.`);
            codeInputRef.current?.focus();
        } catch (err) {
            logAuthFlowWarning("verification_resend_failed", {
                intent,
                source,
                message: err?.message || "unknown_error",
                code: err?.code || null,
                requestId: err?.requestId || null,
                offline: isNetworkError(err),
            });
            setError(getOtpErrorMessage(err));
            setErrorCode(getAuthErrorCode(err) || "");
            setErrorRequestId(getAuthRequestId(err) || "");

        }
    };

    return (
        <AuthShell
            eyebrow="Step 2 of 3"
            title="Enter your verification code"
            description={verificationDescription}
            footer={
                <div className="space-y-3 text-sm text-secondary">
                    <p>{verificationFooter}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        {activeChannel === "sms" ? (
                            <button
                                type="button"
                                onClick={() => {
                                    if (!methodSelection?.email?.available) {
                                        return;
                                    }

                                    navigate("/auth/login-email", {
                                        state: {
                                            allowEmailFallback: true,
                                            phone,
                                            brokerageId,
                                            intent,
                                            source,
                                            returnTo: returnTo || location.pathname,
                                            hasExplicitIntent,
                                            methodSelection,
                                        },
                                    });
                                }}
                                className={`font-semibold rounded ${methodSelection?.email?.available ? "text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2" : "text-subText cursor-not-allowed"}`}
                                disabled={!methodSelection?.email?.available}
                            >
                                Use backup email instead
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => navigate("/auth/recovery", {
                                state: {
                                    phone,
                                    email,
                                    intent,
                                    source,
                                    returnTo,
                                },
                            })}
                            className="font-semibold text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                        >
                            Need account recovery?
                        </button>
                    </div>
                </div>
            }
        >
            <form onSubmit={handleConfirm} noValidate>
                <AuthContextPanel
                    title="Confirm your secure sign-in"
                    items={[
                        {
                            label: "Workspace",
                            value: intentDetails.label,
                        },
                        {
                            label: deliveryTargetLabel,
                            value: deliveryTarget,
                        },
                    ]}
                    helper="Enter the code on this screen to keep the sign-in tied to this device."
                />

                {activeFallbackReason === "sms_delivery_unavailable" ? (
                    <div className="mt-4">
                        <AuthAlert tone="success" title="Backup email used">
                            SMS delivery is temporarily unavailable, so we sent your secure code to the backup email on file instead.
                        </AuthAlert>
                    </div>
                ) : null}

                {activeOtpPreview ? (
                    <div className="mt-4">
                        <AuthAlert tone="info" title="Development test code">
                            {activeTestUserBypass
                                ? "No email or SMS is sent for this test account. "
                                : "Use this non-production verification code. "}
                            Code: <span className="font-mono font-semibold">{activeOtpPreview}</span>
                        </AuthAlert>
                    </div>
                ) : null}

                <div className="mt-6">
                    <label htmlFor="verification-code" className="text-sm font-medium text-foreground">
                        {otpLength}-digit verification code
                    </label>
                    <input
                        id="verification-code"
                        ref={codeInputRef}
                        type="text"
                        inputMode="numeric"
                        pattern={`\\d{${otpLength}}`}
                        autoComplete="one-time-code"
                        maxLength={otpLength}
                        value={otpCode}
                        onChange={handleChange}
                        onPaste={handlePaste}
                        className="mt-4 w-full rounded-2xl border border-lightBorder bg-white px-5 py-4 text-center font-mono text-2xl font-semibold tracking-[0.35em] text-foreground outline-none shadow-sm transition focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:text-3xl"
                    />
                </div>

                <div className="mt-5 flex flex-col gap-3 text-sm">
                    <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendRemaining > 0 || loading}
                        className={`text-left font-semibold ${resendRemaining > 0 || loading ? "text-subText cursor-not-allowed" : "text-primary hover:underline"}`}
                    >
                        {resendRemaining > 0 ? `Resend available in ${resendRemaining}s` : "Resend code"}
                    </button>

                    {resendMessage ? (
                        <AuthAlert tone="success" title="Code sent">
                            {resendMessage}
                        </AuthAlert>
                    ) : null}
                </div>

                {error ? (
                    <div className="mt-4 space-y-2">
                        <AuthAlert tone="error" title="Verification could not continue" live="assertive">
                            {error}
                        </AuthAlert>
                        {errorCode ? (
                            <div className="text-xs text-secondary">
                                Error code: <span className="break-all font-mono text-foreground">{errorCode}</span>
                            </div>
                        ) : null}
                        {errorRequestId ? (
                            <div className="text-xs text-secondary">
                                Request ID: <span className="break-all font-mono text-foreground">{errorRequestId}</span>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row">
                    <SecondaryButton
                        type="button"
                        onClick={handleCancel}
                        className="flex-1 text-lg"
                        disabled={loading}
                    >
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        type="submit"
                        className="flex-1"
                        disabled={loading || !isComplete}
                        aria-busy={loading}
                    >
                        {loading ? "Verifying identity..." : "Continue"}
                    </PrimaryButton>
                </div>
            </form>
        </AuthShell>
    );
}
